import type {Command} from '@codemirror/view'
import {ActionBar, type IconButtonProps} from '@primer/react'
import {use} from 'react'

import {useHybridEditorTelemetry} from '../telemetry'
import {ToolbarContext} from './ToolbarContext'

export interface CustomToolbarButtonProps extends Pick<IconButtonProps, 'icon' | 'disabled' | 'keybindingHint'> {
  ['aria-label']: string
  /** CodeMirror command to execute on click. Can use the `view` parameter to interact with the editor. */
  onClick: Command
  telemetryId?: string
}

export function CustomToolbarButton({
  icon,
  ['aria-label']: ariaLabel,
  disabled,
  keybindingHint,
  onClick: onClickCommand,
  telemetryId,
}: CustomToolbarButtonProps) {
  const {view} = use(ToolbarContext)
  const telemetry = useHybridEditorTelemetry()

  const onClick = () => {
    if (!view) return

    // Toolbar clicks can steal focus, so ensure the editor is focused before invoking CodeMirror commands.
    if (!view.hasFocus) view.focus()

    const handled = onClickCommand(view)

    telemetry.trackToolbarInteraction({
      control: telemetryId ?? ariaLabel ?? 'unknown',
      action: telemetryId ?? ariaLabel ?? 'unknown',
      trigger: 'toolbar',
      isActive: false,
      keybinding: keybindingHint,
      handled,
    })
  }

  return (
    <ActionBar.IconButton
      variant="invisible"
      onMouseDown={e => e.preventDefault()}
      disabled={!view || disabled}
      onClick={onClick}
      icon={icon}
      aria-label={ariaLabel}
      keybindingHint={keybindingHint}
    />
  )
}

try{ CustomToolbarButton.displayName ||= 'CustomToolbarButton' } catch {}